import { useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (p1.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (p1 !== p2) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, newPassword: p1 });
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Reset failed. Please request a new link.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white bg-blueprint-fine flex items-center justify-center px-6">
      <div className="w-full max-w-md border border-ink/10 bg-[#f6f6f6] p-10" data-testid="reset-password-page">
        <img src="/assets/skope-logo-full.png" alt="Skope Kitchens" className="h-7 w-auto" />
        {done ? (
          <div className="mt-8" data-testid="reset-success">
            <CheckCircle2 className="text-skope" size={28} />
            <h1 className="mt-4 font-display font-semibold text-2xl text-ink">Password updated.</h1>
            <p className="mt-2 text-sm text-ink/50">Your old password no longer works. Log in with your new password.</p>
            <button onClick={() => navigate("/admin/login", { replace: true })}
              className="mt-8 w-full bg-skope text-ink font-mono text-xs tracking-[0.2em] uppercase py-4 hover:bg-ink hover:text-white transition-colors"
              data-testid="reset-goto-login">
              Back to Admin Login
            </button>
          </div>
        ) : (
          <>
            <h1 className="mt-8 font-display font-semibold text-2xl text-ink">Create new password</h1>
            {!token ? (
              <p className="mt-4 text-sm text-red-400" data-testid="reset-no-token">
                This reset link is missing its token. Please use the full link from your email, or{" "}
                <Link to="/admin/login" className="text-skope">request a new one</Link>.
              </p>
            ) : (
              <form onSubmit={submit}>
                <label className="block mt-8">
                  <span className="font-mono text-[10px] tracking-[0.25em] text-ink/45 uppercase">New Password</span>
                  <input type="password" value={p1} onChange={(e) => setP1(e.target.value)} required minLength={8}
                    data-testid="reset-new-password"
                    className="mt-2 w-full bg-transparent border-b border-ink/20 focus:border-skope outline-none py-3 text-ink text-sm transition-colors" />
                </label>
                <label className="block mt-6">
                  <span className="font-mono text-[10px] tracking-[0.25em] text-ink/45 uppercase">Confirm New Password</span>
                  <input type="password" value={p2} onChange={(e) => setP2(e.target.value)} required minLength={8}
                    data-testid="reset-confirm-password"
                    className="mt-2 w-full bg-transparent border-b border-ink/20 focus:border-skope outline-none py-3 text-ink text-sm transition-colors" />
                </label>
                {error && <p className="mt-4 text-sm text-red-400" data-testid="reset-error">{error}</p>}
                <button type="submit" disabled={loading}
                  data-testid="reset-submit"
                  className="mt-8 w-full bg-skope text-ink font-mono text-xs tracking-[0.2em] uppercase py-4 hover:bg-ink hover:text-white transition-colors disabled:opacity-50">
                  {loading ? "Updating…" : "Set New Password"}
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminResetPassword;
